import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WordList,
  fetchWordList,
  fetchWordListPreserveCase,
  CustomWordlistData,
  customWordlistToWordList,
  loadCustomWordlist,
} from '../Wordfinder/wordfinderUtils';
import CustomWordlistDialog from '../Wordfinder/CustomWordlistDialog';
import { PathResult, findMorphPaths } from './pathfinderUtils';
import './Pathfinder.less';

const Pathfinder: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [words41k, setWords41k] = useState<WordList | null>(null);
  const [words172k, setWords172k] = useState<WordList | null>(null);
  const [words380k, setWords380k] = useState<WordList | null>(null);
  const [broda, setBroda] = useState<WordList | null>(null);
  const [currentWordList, setCurrentWordList] = useState<WordList>([]);
  const [wordlistSize, setWordlistSize] = useState('41k');

  const [startWord, setStartWord] = useState('');
  const [endWord, setEndWord] = useState('');
  const [maxPathLength, setMaxPathLength] = useState('5');
  const [maxMorph, setMaxMorph] = useState('2');
  const [minWordLength, setMinWordLength] = useState('4');

  const [results, setResults] = useState<PathResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [excludedWords, setExcludedWords] = useState<Set<string>>(new Set());
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [currentMaxResults, setCurrentMaxResults] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 50;

  // Custom wordlist state
  const [customWordlist, setCustomWordlist] = useState<CustomWordlistData | null>(null);
  const [minQuality, setMinQuality] = useState<number | undefined>(undefined);
  const [showCustomDialog, setShowCustomDialog] = useState(false);

  // Fetch word lists on component mount
  useEffect(() => {
    fetchWordList('https://raw.githubusercontent.com/julianeden/english-words/refs/heads/master/40k.txt')
      .then(words => {
        setWords41k(words);
        setCurrentWordList(words);
      })
      .catch(error => console.error('Error fetching 41k word list:', error));

    fetchWordList('https://raw.githubusercontent.com/julianeden/english-words/master/172k.txt')
      .then(words => setWords172k(words))
      .catch(error => console.error('Error fetching 172k word list:', error));

    fetchWordListPreserveCase('https://raw.githubusercontent.com/julianeden/english-words/master/words_alpha.txt')
      .then(words => setWords380k(words))
      .catch(error => console.error('Error fetching 380k word list:', error));

    fetchWordList('https://raw.githubusercontent.com/julianeden/english-words/master/Broda-trimmed.txt')
      .then(words => setBroda(words))
      .catch(error => console.error('Error fetching Broda word list:', error));

    // Load custom wordlist from storage if it exists
    loadCustomWordlist()
      .then(data => {
        if (data) {
          setCustomWordlist(data);
        }
      })
      .catch(error => console.error('Error loading custom wordlist:', error));
  }, []);

  // Update current word list when selection changes
  useEffect(() => {
    switch (wordlistSize) {
      case '41k':
        if (words41k) setCurrentWordList(words41k);
        break;
      case '172k':
        if (words172k) setCurrentWordList(words172k);
        break;
      case '380k':
        if (words380k) setCurrentWordList(words380k);
        break;
      case 'broda':
        if (broda) setCurrentWordList(broda);
        break;
      case 'custom':
        if (customWordlist) {
          setCurrentWordList(customWordlistToWordList(customWordlist, minQuality));
        }
        break;
    }
  }, [wordlistSize, words41k, words172k, words380k, broda, customWordlist, minQuality]);

  const handleWordlistUploaded = (wordlist: CustomWordlistData) => {
    setCustomWordlist(wordlist);
    setWordlistSize('custom');
    setMinQuality(undefined);
    setShowCustomDialog(false);
  };

  const handleWordlistCleared = () => {
    setCustomWordlist(null);
    setWordlistSize('41k'); // Reset to default wordlist
    setMinQuality(undefined);
    setShowCustomDialog(false);
  };

  const handleExcludeWord = (word: string) => {
    // Don't exclude start or end words
    if (word === startWord.toLowerCase() || word === endWord.toLowerCase()) {
      return;
    }

    const newExcluded = new Set(excludedWords);
    newExcluded.add(word);
    setExcludedWords(newExcluded);

    // Automatically rerun the search
    performSearchWithExcluded(newExcluded);
  };

  const handleRemoveExcluded = (word: string) => {
    const newExcluded = new Set(excludedWords);
    newExcluded.delete(word);
    setExcludedWords(newExcluded);

    // Automatically rerun the search
    performSearchWithExcluded(newExcluded);
  };

  const performSearchWithExcluded = (excluded: Set<string>, maxResults: number = currentMaxResults, resetPage: boolean = true) => {
    if (!startWord || !endWord) return;

    setLoading(true);

    // Parse string inputs to numbers
    const maxLen = parseInt(maxPathLength) || 5;
    const maxM = parseInt(maxMorph) || 2;
    const minLen = parseInt(minWordLength) || 4;

    if (resetPage) {
      // Fresh search - clear results and reset page
      setResults([]);
      setCurrentPage(1);
    }
    // If not resetting page, keep existing results visible

    // Use setTimeout to allow UI to update before heavy computation
    setTimeout(() => {
      const searchResult = findMorphPaths(
        startWord.toLowerCase(),
        endWord.toLowerCase(),
        currentWordList,
        maxLen,
        maxM,
        maxResults,
        resetPage ? (path) => {
          // Stream results as they're found (only for fresh searches)
          setResults(prev => [...prev, path]);
        } : undefined,
        undefined,
        excluded,
        minLen
      );

      // Update results with complete set when loading more
      if (!resetPage) {
        setResults(searchResult.paths);
      }

      setHasMoreResults(searchResult.hasMore);
      setLoading(false);
    }, 0);
  };

  const performSearch = () => {
    if (!startWord || !endWord) return;

    // Clear excluded words when starting a new search
    setExcludedWords(new Set());

    // Reset max results to default
    setCurrentMaxResults(50);
    setCurrentPage(1);

    setLoading(true);

    // Parse string inputs to numbers
    const maxLen = parseInt(maxPathLength) || 5;
    const maxM = parseInt(maxMorph) || 2;
    const minLen = parseInt(minWordLength) || 4;

    // Update URL with search parameters
    const params: Record<string, string> = {
      start: startWord,
      end: endWord,
    };
    if (maxLen !== 5) params.maxPathLength = maxLen.toString();
    if (maxM !== 2) params.maxMorph = maxM.toString();
    if (minLen !== 4) params.minWordLength = minLen.toString();
    if (wordlistSize !== '41k') params.wordlistSize = wordlistSize;
    setSearchParams(params);

    // Clear previous results
    setResults([]);

    // Use setTimeout to allow UI to update before heavy computation
    setTimeout(() => {
      const searchResult = findMorphPaths(
        startWord.toLowerCase(),
        endWord.toLowerCase(),
        currentWordList,
        maxLen,
        maxM,
        50,
        (path) => {
          // Stream results as they're found
          setResults(prev => [...prev, path]);
        },
        undefined,
        new Set(),
        minLen
      );

      setHasMoreResults(searchResult.hasMore);
      setLoading(false);
    }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch();
  };

  const handlePreviousSearch = () => {
    window.history.back();
  };

  const handleLoadMore = () => {
    const newMaxResults = currentMaxResults + 50;
    setCurrentMaxResults(newMaxResults);
    setCurrentPage(currentPage + 1);
    performSearchWithExcluded(excludedWords, newMaxResults, false);
  };

  const handleNextPage = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  // Load search params from URL on mount and when URL changes
  useEffect(() => {
    const start = searchParams.get('start');
    const end = searchParams.get('end');
    if (!start || !end) return;

    setStartWord(start);
    setEndWord(end);
    setMaxPathLength(searchParams.get('maxPathLength') || '5');
    setMaxMorph(searchParams.get('maxMorph') || '2');
    setMinWordLength(searchParams.get('minWordLength') || '4');
    setWordlistSize(searchParams.get('wordlistSize') || '41k');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Perform search when URL params change and wordlist is ready
  useEffect(() => {
    const start = searchParams.get('start');
    const end = searchParams.get('end');
    if (!start || !end || currentWordList.length === 0) return;

    const maxLen = searchParams.get('maxPathLength') ? parseInt(searchParams.get('maxPathLength')!) : 5;
    const maxM = searchParams.get('maxMorph') ? parseInt(searchParams.get('maxMorph')!) : 2;
    const minLen = searchParams.get('minWordLength') ? parseInt(searchParams.get('minWordLength')!) : 4;

    setLoading(true);
    setResults([]);
    setExcludedWords(new Set());
    setCurrentMaxResults(50);
    setCurrentPage(1);

    setTimeout(() => {
      const searchResult = findMorphPaths(
        start,
        end,
        currentWordList,
        maxLen,
        maxM,
        50,
        (path) => {
          // Stream results as they're found
          setResults(prev => [...prev, path]);
        },
        undefined,
        new Set(),
        minLen
      );

      setHasMoreResults(searchResult.hasMore);
      setLoading(false);
    }, 0);
  }, [searchParams, currentWordList]);

  return (
    <div className="pathfinderContainer">
      <div className="pathfinderFormContainer">
        <form onSubmit={handleSubmit}>
          <div className="pathfinderFormGroup">
            <label htmlFor="startWord">Start Word:</label>
            <input
              type="text"
              id="startWord"
              value={startWord}
              onChange={(e) => setStartWord(e.target.value)}
              autoComplete="off"
              required
            />
          </div>
          <div className="pathfinderFormGroup">
            <label htmlFor="endWord">End Word:</label>
            <input
              type="text"
              id="endWord"
              value={endWord}
              onChange={(e) => setEndWord(e.target.value)}
              autoComplete="off"
              required
            />
          </div>
          <div className="pathfinderFormGroup">
            <label htmlFor="maxPathLength">Max Path Length:</label>
            <input
              type="number"
              id="maxPathLength"
              value={maxPathLength}
              onChange={(e) => setMaxPathLength(e.target.value)}
              placeholder="5"
              min="2"
              max="10"
            />
          </div>
          <div className="pathfinderFormGroup">
            <label htmlFor="maxMorph">Max Morph:</label>
            <input
              type="number"
              id="maxMorph"
              value={maxMorph}
              onChange={(e) => setMaxMorph(e.target.value)}
              placeholder="2"
              min="1"
              max="5"
            />
          </div>
          <div className="pathfinderFormGroup">
            <label htmlFor="minWordLength">Min Word Length:</label>
            <input
              type="number"
              id="minWordLength"
              value={minWordLength}
              onChange={(e) => setMinWordLength(e.target.value)}
              placeholder="4"
              min="1"
              max="20"
            />
          </div>
          <div className="pathfinderFormGroup">
            <label htmlFor="wordlistSize">Use wordlist with size:</label>
            <select
              id="wordlistSize"
              value={wordlistSize}
              onChange={(e) => setWordlistSize(e.target.value)}
            >
              <option value="41k">41k</option>
              <option value="172k">172k</option>
              <option value="380k">380k</option>
              <option value="broda">Broda (280k, includes phrases + names)</option>
              {customWordlist && (
                <option value="custom">
                  Custom ({customWordlist.wordCount.toLocaleString()} words)
                </option>
              )}
            </select>
          </div>
          <div className="pathfinderFormGroup">
            <button
              type="button"
              className="pathfinderCustomButton"
              onClick={() => setShowCustomDialog(true)}
            >
              {customWordlist ? 'Change custom wordlist' : 'Use custom wordlist'}
            </button>
          </div>
          {customWordlist?.hasQualityRatings && wordlistSize === 'custom' && (
            <div className="pathfinderFormGroup">
              <label htmlFor="minQuality">Minimum word quality:</label>
              <select
                id="minQuality"
                value={minQuality ?? ''}
                onChange={(e) => setMinQuality(e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">All qualities</option>
                {customWordlist.qualityLevels.map(level => (
                  <option key={level} value={level}>
                    {level}+
                  </option>
                ))}
              </select>
            </div>
          )}
          <br/>
          <button className="pathfinderSubmitButton" type="submit">Find Paths</button>
          <button className="pathfinderPreviousButton" type="button" onClick={handlePreviousSearch}>
            Previous search
          </button>
        </form>
        <div className="pathfinderInfoBox">
          <p><strong>Tip:</strong> Click any word in a result path to exclude it and rerun the search.</p>
        </div>
      </div>
      <div className="pathfinderResultsContainer">
        {excludedWords.size > 0 && (
          <div className="pathfinderExcludedWords">
            <p><strong>Excluded words:</strong></p>
            <div className="pathfinderExcludedList">
              {Array.from(excludedWords).map(word => (
                <span key={word} className="pathfinderExcludedWord" onClick={() => handleRemoveExcluded(word)}>
                  {word} ✕
                </span>
              ))}
            </div>
          </div>
        )}
        {loading ? (
          <>
            <p>Searching for paths from "{startWord}" to "{endWord}" with max morph {parseInt(maxMorph) || 2}...</p>
            <div className="pathfinderLoadingSpinner"></div>
          </>
        ) : results.length > 0 ? (
          <>
            <div className="pathfinderResultsCount">
              <span>
                {hasMoreResults ? 'At least ' : ''}{results.length} path{results.length !== 1 ? 's' : ''} found
              </span>
              {hasMoreResults && (
                <button className="pathfinderLoadMoreButton" onClick={handleLoadMore}>
                  Find 50 more paths
                </button>
              )}
            </div>
            {results.length > resultsPerPage && (
              <div className="pathfinderPagination">
                <button
                  className="pathfinderPaginationButton"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                <span className="pathfinderPaginationInfo">
                  Page {currentPage} of {Math.ceil(results.length / resultsPerPage)}
                </span>
                <button
                  className="pathfinderPaginationButton"
                  onClick={handleNextPage}
                  disabled={currentPage >= Math.ceil(results.length / resultsPerPage)}
                >
                  Next →
                </button>
              </div>
            )}
            {results.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage).map((result, index) => (
              <div key={index} className="pathfinderSearchResult">
                {result.path.map((word, wordIndex) => (
                  <span key={wordIndex}>
                    {wordIndex > 0 && <span className="pathfinderArrow"> → </span>}
                    <span
                      className={`pathfinderPathWord ${
                        word === startWord.toLowerCase() || word === endWord.toLowerCase()
                          ? 'pathfinderEndpoint'
                          : 'pathfinderClickable'
                      }`}
                      onClick={() => handleExcludeWord(word)}
                    >
                      {word}
                    </span>
                  </span>
                ))}
              </div>
            ))}
          </>
        ) : startWord && endWord && !loading ? (
          <p>No paths found</p>
        ) : (
          <p>Enter start and end words to find morph paths</p>
        )}
      </div>

      {/* Custom Wordlist Upload Dialog */}
      {showCustomDialog && (
        <CustomWordlistDialog
          existingWordlist={customWordlist}
          onClose={() => setShowCustomDialog(false)}
          onWordlistUploaded={handleWordlistUploaded}
          onWordlistCleared={handleWordlistCleared}
        />
      )}
    </div>
  );
};

export default Pathfinder;
